import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiDataService } from 'src/app/shared/services/api.data.service';
import {
  IssueSyncIdsResponse, IssueSyncResponse, IssueSyncRowsResponse, IssueSyncScope,
} from './issue-sync-models';
import {
  IssueSyncTransport, SyncCursorRejectedError, SyncIdsRejectedError, SyncScopeDeniedError,
} from './issue-sync-transport';

// The Angular adapter for the sync transport. The port and its error types live in
// issue-sync-transport.ts rather than here, because the replica depends on them and
// would otherwise import Angular and the NGXS store through this file.

function statusOf(err: unknown): number {
  return (err as { status?: number } | null)?.status ?? 0;
}

@Injectable({ providedIn: 'root' })
export class IssueSyncApi implements IssueSyncTransport {
  private readonly apiController = 'issues';

  constructor(private readonly api: ApiDataService) {}

  async fetchDelta(scope: IssueSyncScope, cursor: string, limit: number): Promise<IssueSyncResponse> {
    const url = this.api.buildUrlWithParams(`${this.apiController}/sync`, {
      ...this.scopeParams(scope),
      // Omitted rather than sent empty, so "start at the beginning" is expressed by
      // the absence of a cursor rather than by a value the server has to interpret.
      ...(cursor ? { cursor } : {}),
      limit,
    });
    return this.send<IssueSyncResponse>(url);
  }

  async fetchIds(scope: IssueSyncScope): Promise<IssueSyncIdsResponse> {
    const url = this.api.buildUrlWithParams(
      `${this.apiController}/sync/ids`, this.scopeParams(scope),
    );
    return this.send<IssueSyncIdsResponse>(url);
  }

  // Reads the named rows as they stand now, with no safety lag, which is what lets a
  // write confirm in seconds instead of waiting for the feed to release it.
  //
  // No `scope` is sent on purpose. These ids are rows the client already holds, and the
  // question is whether it may still hold them — so the caller's whole permitted scope
  // is the right basis. Narrowing to the working scope would report a row synced under
  // a different scope as removed and evict it.
  async fetchRowsByIds(issueIds: string[]): Promise<IssueSyncRowsResponse> {
    const url = this.api.buildUrlWithParams(`${this.apiController}/sync/rows`, {
      ids: issueIds.join(','),
    });
    return this.send<IssueSyncRowsResponse>(url, () => new SyncIdsRejectedError());
  }

  // Group ids go as one comma-separated value. Passing the array would emit a
  // repeated `scope` parameter, which binds to a single string on the server and
  // would drop every group but one — narrowing the scope with no error anywhere.
  private scopeParams(scope: IssueSyncScope): Record<string, string> {
    const params: Record<string, string> = {};
    const groups = [...scope.groupIds].filter(id => !!id).sort().join(',');
    if (groups) params['scope'] = groups;
    if (scope.fingerprint) params['fingerprint'] = scope.fingerprint;
    return params;
  }

  // `onBadRequest` names what a 400 means for the route being called. The routes do not
  // agree: on the feed it is a cursor this server did not issue, on the by-id read it is
  // a malformed id. Treating them alike would answer a bad id by re-walking a scope.
  private async send<T>(url: string, onBadRequest: () => Error = () => new SyncCursorRejectedError()): Promise<T> {
    try {
      return await firstValueFrom(this.api.get<T>(url));
    } catch (err) {
      const status = statusOf(err);
      if (status === 403) throw new SyncScopeDeniedError();
      if (status === 400) throw onBadRequest();
      throw err;
    }
  }
}
